import apiClient from './client';

export interface MonthlyRevenue {
  month: string;
  invoice_count: number;
  invoiced: number;
  collected: number;
  outstanding: number;
}

export interface RevenueReport {
  monthly: MonthlyRevenue[];
  ytd: { invoiced: number; collected: number; outstanding: number; count: number };
}

export interface ArInvoice {
  id: number;
  invoice_number: string;
  client_name: string;
  invoice_date: string;
  due_date: string;
  total: number;
  balance_due: number;
  days_overdue: number;
  aging_bucket: string;
}

export interface ArAgingReport {
  invoices: ArInvoice[];
  buckets: Record<string, number>;
}

export interface ProfitabilityRow {
  labour_cost?: number;
  expenses_total?: number;
  true_cost?: number;
  burn_pct?: number;
  projected_margin_pct?: number;
  id: number;
  title: string;
  status: string;
  client_name: string;
  revenue_invoiced: number;
  revenue_collected: number;
  budget: number;
  actual_cost: number;
  variance: number;
}

export interface UtilizationRow {
  id: number;
  name: string;
  email: string;
  entry_count: number;
  total_hours: number;
  avg_hours_per_day: number;
}

// ── GST summary (CR #110) ───────────────────────────────────────────────────

export interface GstQuarter {
  quarter: string;
  invoice_count: number;
  invoiced_subtotal: number;
  gst_collected: number;
  gst_explicit: number;
  expense_count: number;
  expense_tax_scanned: number;
  gst_itc: number;
  net_owed: number;
}

export interface GstReport {
  year: number;
  quarters: GstQuarter[];
  totals: { gst_collected: number; gst_itc: number; net_owed: number };
  unassigned_expenses: { count: number; tax: number };
}

export const getGstReport = async (year?: number): Promise<GstReport> => {
  const { data } = await apiClient.get('/reports/gst', { params: { year } });
  return data.data;
};

export const getRevenueReport = async (year?: number): Promise<RevenueReport> => {
  const { data } = await apiClient.get('/reports/revenue', { params: { year } });
  return data.data;
};

export const getArAgingReport = async (): Promise<ArAgingReport> => {
  const { data } = await apiClient.get('/reports/ar-aging');
  return data.data;
};

export const getProfitabilityReport = async (): Promise<ProfitabilityRow[]> => {
  const { data } = await apiClient.get('/reports/profitability');
  return data.data;
};

export const getCrewUtilizationReport = async (days?: number): Promise<UtilizationRow[]> => {
  const { data } = await apiClient.get('/reports/crew-utilization', { params: { days } });
  return data.data;
};

export interface WinLossRow {
  client_id: number | null;
  client: string;
  won: number;
  lost: number;
  pending: number;
  revenue_won: number;
  revenue_lost: number;
  total_quotes: number;
}
export interface WinLossReport {
  clients: WinLossRow[];
  totals: { won: number; lost: number; pending: number; revenue_won: number; revenue_lost: number; win_rate: number };
}
export const getWinLossReport = async (): Promise<WinLossReport> => {
  const { data } = await apiClient.get('/reports/win-loss');
  return data.data;
};
